import { getNowInTimeZone } from '@chronosphereio/core';
import { pickersCalendarHeaderClasses, type RangePosition } from '@mui/x-date-pickers-pro';
import { endOfDay, endOfMonth, isSameDay, isSameMonth, startOfDay, startOfMonth } from 'date-fns';
import { MouseEvent as ReactMouseEvent, useCallback, useRef, useState } from 'react';

/**
 * Computes the month range for selecting an entire month.
 * If the month is the current month, selects up to today instead of end of month.
 * Clamps the selection to minDate/maxDate if provided.
 */
function computeMonthRange(
  displayedMonth: Date,
  now: Date,
  minDate?: Date,
  maxDate?: Date
): { end: Date; start: Date } {
  let monthStart = startOfMonth(displayedMonth);
  const isCurrentMonth = isSameMonth(displayedMonth, now);
  let monthEnd = isCurrentMonth ? endOfDay(now) : endOfMonth(displayedMonth);

  // Clamp to minDate/maxDate constraints
  if (minDate && monthStart < minDate) {
    monthStart = startOfDay(minDate);
  }
  if (maxDate && monthEnd > maxDate) {
    monthEnd = endOfDay(maxDate);
  }
  monthEnd.setMilliseconds(0);

  return { end: monthEnd, start: monthStart };
}

export interface UseCalendarRangeInteractionOptions {
  calendarStart: Date | null;
  onMonthSelect: (range: { end: Date; start: Date }) => void;
  maxDate?: Date;
  minDate?: Date;
  timeZone?: string;
}

/**
 * Hook that manages shared calendar range interaction logic including:
 * - Tracking range position (start/end selection)
 * - Managing refs for time modification state
 * - Handling month navigation and month label clicks
 * - Processing calendar wrapper click events
 */
export function useCalendarRangeInteraction({
  calendarStart,
  timeZone,
  minDate,
  maxDate,
  onMonthSelect,
}: UseCalendarRangeInteractionOptions) {
  // Track when we're starting a new range selection to ignore stale end date from MUI
  const isSelectingNewStart = useRef(false);
  // We need to save the end date to retain modified time because the calendar-based range picker
  // has an interim state after selecting the first date where the end date needs to be null, so we
  // cannot pull the previously saved time from the `timeRange`.
  const modifiedEndTime = useRef<Date | null>(null);
  // Track range position to know if user is selecting start or end date
  const [rangePosition, setRangePosition] = useState<RangePosition>('start');
  // Ref to track the currently displayed month in the calendar
  const displayedMonthRef = useRef<Date>(calendarStart ?? new Date());
  // Sync displayedMonthRef with calendarValue.start on render
  const prevCalendarStartRef = useRef(calendarStart);

  // Only update the displayed month if the calendar date has changed
  if (calendarStart && (!prevCalendarStartRef.current || !isSameDay(calendarStart, prevCalendarStartRef.current))) {
    displayedMonthRef.current = calendarStart;
    prevCalendarStartRef.current = calendarStart;
  }

  /**
   * Handle month change from the calendar navigation arrows.
   * Updates the displayed month ref so clicking the label selects the correct month.
   */
  const handleMonthChange = useCallback((newMonth: Date | null) => {
    if (newMonth) {
      displayedMonthRef.current = newMonth;
    }
  }, []);

  /**
   * Handle clicking on the month label in the calendar header.
   * Selects the entire month (first day to last day) in the time range.
   * If the month is the current month, selects up to today instead of end of month.
   * Clamps the selection to minDate/maxDate if provided.
   */
  const handleMonthLabelClick = useCallback(() => {
    const now = getNowInTimeZone(timeZone);
    const range = computeMonthRange(displayedMonthRef.current, now, minDate, maxDate);
    onMonthSelect(range);
    isSelectingNewStart.current = false;
  }, [timeZone, minDate, maxDate, onMonthSelect]);

  /*
   * Handle clicks on the calendar wrapper to intercept month label clicks.
   * this is a bit of a hack until we get to v7 of MUI and can use the slot/slotProps:
   * https://v7.mui.com/x/api/date-pickers/date-range-calendar/#slots
   */
  const handleCalendarWrapperClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      // Check if the click was on the month label or its container
      if (
        target.closest(`.${pickersCalendarHeaderClasses.labelContainer}`) ||
        target.closest(`.${pickersCalendarHeaderClasses.label}`)
      ) {
        event.preventDefault();
        event.stopPropagation();
        handleMonthLabelClick();
      }
    },
    [handleMonthLabelClick]
  );

  const handleRangePositionChange = useCallback((newPosition: RangePosition) => {
    setRangePosition(newPosition);

    // When position changes to 'end', it means user just selected a start date
    // and is now picking the end date. Set the isSelectingNewStart flag so
    // handleDateRangeChange knows to ignore any stale end date from MUI.
    if (newPosition === 'end') {
      isSelectingNewStart.current = true;
      modifiedEndTime.current = null;
    }
  }, []);

  const resetInteractionState = useCallback(() => {
    modifiedEndTime.current = null;
    isSelectingNewStart.current = false;
  }, []);

  return {
    displayedMonthRef,
    handleCalendarWrapperClick,
    handleMonthChange,
    handleRangePositionChange,
    isSelectingNewStart,
    modifiedEndTime,
    rangePosition,
    resetInteractionState,
  };
}
